//@ts-nocheck
import React, { useEffect, useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import PersonAddAltSharpIcon from "@mui/icons-material/PersonAddAltSharp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import MDButton from "components/MDButton";
import { get, post } from "requests";
import UserAvatar from "../../applications/tabs/submissions/UserAvatar";
import Tooltip from "@mui/material/Tooltip";
import { useStoreActions, useStoreState } from "easy-peasy";
import FullPageLoader from "components/Loader/FullPage";

interface AssignedUser {
  fullName: string;
  firstName: string;
  lastName: string;
  role: string;
  color: string;
  userId?: number;
}

interface User {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
}

interface PipelineAvatarProps {
  data: any;
}

type Role = "ISO Representative" | "Senior Underwriter" | "Junior Underwriter" | "DELETE ROLE";

const PipelineAvatar: React.FC<PipelineAvatarProps> = ({
  data,
  refreshApplications,
  editableRoles,
  assignableRoles,
}) => {
  const [open, setOpen] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);
  const [isUpdated, setIsUpdated] = useState<boolean>(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const userList = useStoreState((state) => state.applications.users);
  const fetchUsers = useStoreActions((actions) => actions.applications.fetchUsers);

  useEffect(() => {
    const fetchUsersRole = async () => {
      try {
        let usersRoleResponse = {};

        try {
          if (data?.assignedUsers) {
            usersRoleResponse = JSON.parse(data?.assignedUsers);
          }
        } catch (error) {
          usersRoleResponse = {};
        }

        const updatedAssignedUsers: AssignedUser[] = [];

        const capitalizeWords = (str: string) =>
          str
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

        Object.entries(usersRoleResponse).forEach(([rawRole, users]) => {
          const role = capitalizeWords(rawRole);
          users.forEach(({ id, name }) => {
            const [firstName, ...lastNameArr] = name.split(" ");
            const lastName = lastNameArr.join(" ");

            updatedAssignedUsers.push({
              fullName: name,
              firstName,
              lastName,
              role,
              color: getRandomColor(firstName),
              userId: id,
            });
          });
        });

        setAssignedUsers(updatedAssignedUsers);
      } catch (error) {
        console.error("Error fetching usersRole:", error);
      }
    };
    fetchUsersRole();
  }, [data]);

  const handleClickOpen = async () => {
    try {
      setLoading(true);
      setOpen(true);
      if (!userList || userList?.length == 0) {
        await fetchUsers();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setSelectedRole("");
    setErrorMessage("");
    setShowError(false);
    if (isUpdated) {
      refreshApplications();
      setIsUpdated(false);
    }
  };

  const handleUserChange = (newValue: any) => {
    setSelectedUser(newValue);
  };

  const handleRoleChange = async (index: number, newRole: string) => {
    const user = assignedUsers[index];
    if (newRole === "DELETE ROLE") {
      const success = await deleteUserRole(data?.id, user?.userId!, user?.role);
      if (success) {
        const updatedAssignedUsers = assignedUsers.filter((_: any, i: any) => i !== index);
        setAssignedUsers(updatedAssignedUsers);
      }
    } else {
      const updatedUsers = [...assignedUsers];
      updatedUsers[index].role = newRole;
      setAssignedUsers(updatedUsers);

      if (user.userId) {
        await assignUserRole(newRole as string, data?.id, user.userId);
      } else {
        console.error("User ID is missing for:", user.fullName);
      }
    }
  };

  const handleNewRoleChange = (event: SelectChangeEvent<string>) => {
    setSelectedRole(event.target.value);
  };

  const assignUserRole = async (role: Role, applicationId: number, userId: number) => {
    try {
      let endpoint;
      switch (role) {
        case "ISO Representative":
          endpoint = "setRepresentativeUser";
          break;
        case "Senior Underwriter":
          endpoint = "setSeniorUnderwriter";
          break;
        case "Junior Underwriter":
          endpoint = "setJuniorUnderwriter";
          break;
        default:
          endpoint = "setUserRoles";
      }

      if (endpoint === "setUserRoles") {
        const response = await post(endpoint, { applicationId, userId, roleName: role });
        console.log(`${role} Set:`, response.data);
      } else {
        const response = await post(endpoint, { applicationId, userId });
        console.log(`${role} Set:`, response.data);
      }
    } catch (error) {
      console.error(`Error setting ${role}:`, error);
    }
  };

  const setRepresentativeUser = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setRepresentativeUser", {
        applicationId,
        userId,
      });
      console.log("Representative User Set:", response.data);
    } catch (error) {
      console.error("Error setting Representative User:", error);
    }
  };

  const setJuniorUnderwriter = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setJuniorUnderwriter", {
        applicationId,
        userId,
      });
      console.log("Junior Underwriter Set:", response.data);
    } catch (error) {
      console.error("Error setting Junior Underwriter:", error);
    }
  };

  const setSeniorUnderwriter = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setSeniorUnderwriter", {
        applicationId,
        userId,
      });
      console.log("Senior Underwriter Set:", response.data);
    } catch (error) {
      console.error("Error setting Senior Underwriter:", error);
    }
  };

  const deleteUserRole = async (applicationId: number, userId: number, roleType: string) => {
    try {
      const response = await post("/delete/userRoles", { applicationId, userId, roleType });
      console.log("User Role Deleted:", response.data);
      return true;
    } catch (error) {
      console.error("Error deleting User Role:", error);
      return false;
    }
  };

  const assignRole = async () => {
    if (!selectedUser || !selectedRole) {
      setErrorMessage("Please select a user and a role.");
      setShowError(true);
      return;
    }
    const user = userList?.find((user: any) => user.id === selectedUser.id);
    if (user) {
      const newAssignedUser = {
        fullName: user.fullName,
        firstName: user.firstName,
        lastName: user.lastName,
        role: selectedRole,
        color: getRandomColor(user?.firstName),
        userId: user.id,
      };

      const updatedAssignedUsers = [...assignedUsers, newAssignedUser];
      setAssignedUsers(updatedAssignedUsers);
      try {
        switch (selectedRole) {
          case "ISO Representative":
            await setRepresentativeUser(data?.id, user.id);
            break;
          case "Junior Underwriter":
            await setJuniorUnderwriter(data?.id, user.id);
            break;
          case "Senior Underwriter":
            await setSeniorUnderwriter(data?.id, user.id);
            break;
          default:
            await post("setUserRoles", {
              applicationId: data?.id,
              userId: user.id,
              roleName: selectedRole,
            });
        }
        setSelectedUser(null);
        setSelectedRole("");
        setIsUpdated(true);
      } catch (error) {
        console.error("Error assigning role:", error);
      }
    }
  };

  const getRandomColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);

    const saturation = 80;
    const lightness = 40;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  return (
    <div>
      {loading && <FullPageLoader />}
      <IconButton onClick={handleClickOpen}>
        {assignedUsers.length > 0 ? (
          <AvatarGroup
            max={3}
            sx={{
              "& .MuiAvatar-root": {
                width: 30,
                height: 30,
                fontSize: 15,
                fontWeight: "bold",
                background: "grey",
              },
            }}
          >
            {assignedUsers?.map((user: any, idx: any) => (
              <Tooltip key={idx} title={`${user.fullName} - ${user.role}`}>
                <UserAvatar
                  firstName={user.firstName}
                  lastName={user.lastName}
                  color={user.color}
                  isFromPipeline={true}
                />
              </Tooltip>
            ))}
          </AvatarGroup>
        ) : (
          <Avatar
            sx={{
              backgroundColor: "rgba(47, 146, 159, 0.8)",
              width: 25,
              height: 25,
              marginLeft: "3px",
            }}
          >
            <PersonAddAltSharpIcon
              sx={{ width: 15, height: 15, color: "white", marginLeft: "4px" }}
            />
          </Avatar>
        )}
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "525px",
          },
        }}
      >
        <DialogTitle> {`Assigned Users (${data?.applicationNo})`}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1} marginBottom={2}>
            {assignedUsers?.map((user: any, index: any) => (
              <Box key={index} display="flex" alignItems="center" gap={3} paddingX={1}>
                <UserAvatar
                  firstName={user.firstName}
                  lastName={user.lastName}
                  color={user.color}
                />
                <Box
                  sx={{
                    width: "150px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontFamily: "'Roboto', sans-serif",
                      color: "#333",
                      marginTop: "8px",
                      marginLeft: "6px",
                    }}
                  >
                    {user.fullName}
                  </Typography>
                </Box>

                <Select
                  value={user.role}
                  onChange={(event) => handleRoleChange(index, event.target.value as Role)}
                  displayEmpty
                  IconComponent={(props) => <KeyboardArrowDownIcon />}
                  sx={{
                    fontSize: "15px",
                    fontFamily: "'Roboto', sans-serif",
                    color: "#333",
                    marginTop: "8px",
                    marginLeft: "55px",
                    width: "200px",
                    borderBottom: "2px solid #ccc",
                    paddingBottom: "4px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  {editableRoles?.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Box>

          {/* Add User Section */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: "#2c2c2c", width: 39, height: 39, marginLeft: "8px" }}>
              <PersonAddAltSharpIcon
                sx={{ width: 29, height: 29, color: "white", marginLeft: "4px" }}
              />
            </Avatar>

            <Autocomplete
              options={userList || []}
              getOptionLabel={(option) => option.fullName || ""}
              value={selectedUser}
              onChange={(event, newValue) => handleUserChange(newValue)}
              sx={{
                width: 250,
                minWidth: 150,
                maxWidth: 300,
                marginRight: "30px",
                fontStyle: "italic",
                fontSize: "14px",

                "& .MuiInputBase-root": {
                  height: 35,
                  borderBottom: "1px solid #ccc",
                  fontStyle: "italic",
                  fontSize: "14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& input": {
                  fontSize: "14px",
                  fontStyle: "italic",
                },
              }}
              popupIcon={<KeyboardArrowDownIcon />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a User"
                  variant="outlined"
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.fullName === value?.fullName}
            />

            {/* Job Role Dropdown */}
            <Select
              value={selectedRole}
              onChange={handleNewRoleChange}
              displayEmpty
              IconComponent={(props) => <KeyboardArrowDownIcon />}
              sx={{
                height: 40,
                width: "200px",
                minWidth: "150px",
                maxWidth: "300px",
                fontStyle: "italic",
                borderBottom: "1px solid #ccc",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="" disabled>
                Job Role
              </MenuItem>
              {assignableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {showError && (
            <Typography
              sx={{
                color: "error.main",
                fontSize: "0.875rem",
                marginTop: "15px",
                marginLeft: "20px",
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <MDButton
              onClick={assignRole}
              variant="contained"
              color="primary"
              size="small"
              sx={{ padding: "4px 12px", fontSize: "0.75rem", marginRight: "10px" }}
            >
              Assign Role
            </MDButton>
            <MDButton
              onClick={handleClose}
              variant="contained"
              color="error"
              size="small"
              sx={{ padding: "4px 12px", fontSize: "0.75rem", marginRight: "10px" }}
            >
              Close
            </MDButton>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PipelineAvatar;
